const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/ReportPage/ReportPDFDialog.tsx';
import { useTheme } from '@mui/material/styles';
import { css, cx } from '@emotion/css';
import { DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMemo } from 'react';
import { Report } from '@cistec/common_web/defs/kisim';
import { DocumentReadConfirmation } from './DocumentReadConfirmation';
import { PDFDocumentController } from '@cistec/common_web/components/PDFViewer/PDFDocumentController';
import { PDFViewerProvider } from '@cistec/common_web/components/PDFViewer/PDFViewerProvider';
import { PDFDocument } from '@cistec/common_web/components/PDFViewer/PDFDocument';
import { saveSignedPDF } from 'App/actions/documents';
import { useReportInfo } from './hooks';
import { useDispatch } from 'react-redux';
import { ResponsiveDialog } from '@cistec/common_web/components/ResponsiveDialog/ResponsiveDialog';

import { usePDF } from 'App/store/documents';

interface Props {
    report: Report;
    onClose: () => void;
}

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            dialogTitle: css`
                & > .documentTitle {
                    flex: none;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    vertical-align: middle;

                    ${theme.breakpoints.down('lg')} {
                        display: none;
                    }
                }
            `,
            pdfViewer: css`
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                display: flex;
                flex-direction: column;
                border-top: 1px solid ${theme.palette.grey['500']};
            `,
            pdfDocument: css`
                flex: auto;
            `,
            bottomBar: css`
                display: flex;
                justify-content: flex-end;
                align-items: center;
                gap: ${theme.spacing(1)};
                height: ${theme.spacing(7)};
                flex: none;
                padding: 0 ${theme.spacing(2)};
                border-top: 1px solid #e2e2e2;
            `,
        }),
        [theme]
    );
}

export const ReportPDFDialog = (props: Props) => {
    const classes = useCSSClasses();
    const { report } = props;

    const handleDialogClose = () => {
        props.onClose();
    };

    const { reportToSignInfo, isDocumentRead, needsSignature, needsToBeRead, imageInfo } = useReportInfo(report);

    // FIXME: should really look into PDF loading, because it shouldnt try to load a PDF from two endpoints
    // @ts-ignore
    const { data: thePDF } = usePDF('Image', imageInfo.code, imageInfo.sender);
    const { data: reportPDF } = usePDF('PatReports', report.REPORTNR, report.SENDER);

    const dispatch = useDispatch<any>();

    const pdf = thePDF || reportPDF;

    if (!pdf) return null;

    const text = report.NAME;

    const handleSave = (pdfString: string) => {
        // FIXME: sender or code could technically be undefined....
        // @ts-ignore
        dispatch(saveSignedPDF(imageInfo.sender, imageInfo.code, reportToSignInfo.WORKID, pdfString));
    };

    const renderBottomBar = () => (
        <div className={classes.bottomBar}>
            {needsSignature ? (
                <PDFDocumentController
                    showPaging={false}
                    showZoom={false}
                    showDownload={false}
                    showSignatureControls={true}
                />
            ) : null}
            {needsToBeRead || isDocumentRead ? <DocumentReadConfirmation report={props.report} /> : null}
        </div>
    );

    return (
        <PDFViewerProvider documentTitle={text} key={thePDF ? 'imagePdf' : 'reportPdf'}>
            <ResponsiveDialog
                onClose={handleDialogClose}
                allowClosing={false}
                sizeSettings={{ width: 'calc(100vw - 40px)', height: 'calc(100vh - 40px)' }}
            >
                <DialogTitle className={classes.dialogTitle}>
                    <div className="documentTitle" key="title">
                        {text}
                    </div>
                    <PDFDocumentController key="documentController" />
                    <IconButton color="inherit" onClick={handleDialogClose} key="closeButton">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <div className={cx('PDFViewer', classes.pdfViewer)}>
                        <PDFDocument className={classes.pdfDocument} onSave={handleSave} pdfASCIIString={pdf} />

                        {needsSignature || needsToBeRead || isDocumentRead ? renderBottomBar() : null}
                    </div>
                </DialogContent>
            </ResponsiveDialog>
        </PDFViewerProvider>
    );
};
